import { omit } from "lodash-es";
import objectHash from "object-hash";
import { usePostHog } from "posthog-js/react";
import { Dispatch, SetStateAction, useState } from "react";

import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import { pushSuccess } from "../../slices/notifications";
import { getRouteFormProperty } from "../../slices/routes";
import { useDispatch, useSelector } from "../../store";
import {
  Route,
  useCreateRouteMutation,
  useUpdateRouteMutation,
} from "../../store/pomeriumApi";

export const useRouteFormFunctions = (
  routeId: string | undefined,
  setShowBackWarning: Dispatch<SetStateAction<boolean>>,
) => {
  const posthog = usePostHog();
  const { form, routeHash } = useSelector((state) => state.routes);
  const [saving, setSaving] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigateWithSearch();
  const from = useSelector(getRouteFormProperty("from", "")) as string;
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [addRoute] = useCreateRouteMutation();
  const [updateRoute] = useUpdateRouteMutation();

  const isHttps = from?.includes("https");

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let resp;
      if (routeId) {
        resp = await updateRoute({
          organizationId,
          routeId,
          routeProperties: omit(form, ["updatedAt", "createdAt"]) as Route,
        }).unwrap();
        posthog.capture("route_saved");
      } else {
        resp = await addRoute({
          organizationId,
          routeProperties: { ...form, namespaceId: clusterId } as Route,
        }).unwrap();
      }
      if (resp?.id) {
        dispatch(pushSuccess("Route successfully saved!"));
        if (!routeId) {
          const urlObj = new URL(resp.from);
          posthog.capture("new_route_created", { protocol: urlObj.protocol });
        }
        navigate("/app/management/routes", { replace: true });
      }
    } catch {
      // handled in errorMiddleware.ts
    } finally {
      setSaving(false);
    }
  };

  const handleClickBack = (): void => {
    if (routeHash !== objectHash(form)) {
      setShowBackWarning(true);
    } else {
      navigate("/app/management/routes", { replace: true });
    }
  };

  const shouldSave = (): boolean => {
    return objectHash(form) !== routeHash && !saving && !!form?.name;
  };
  return {
    shouldSave,
    handleClickBack,
    handleSubmit,
    isHttps,
  };
};
